// src/reporting/pdf/addSourcesUses.js

import { PDF_CONTENT_GAP, addSectionTitle, ensurePageSpace } from "./pdfLayout";
import { fmtCurrency } from "./pdfFormatters";

const ROW_HEIGHT = 15;
const WRAPPED_LINE_HEIGHT = 11;
const DETAIL_GROUP_GAP = 4;
const DETAIL_CONTINUATION_INDENT = 6;

function getRowLayout(doc, row, width) {
  const detailIndent = row.isDetail ? 10 : 0;
  const labelWidth =
    width - 90 - detailIndent - (row.isDetail ? DETAIL_CONTINUATION_INDENT : 0);
  const labelLines = doc.splitTextToSize(
    String(row.label || "—"),
    labelWidth
  );

  return {
    detailIndent,
    continuationIndent: row.isDetail ? DETAIL_CONTINUATION_INDENT : 0,
    labelLines,
    height:
      Math.max(ROW_HEIGHT, labelLines.length * WRAPPED_LINE_HEIGHT) +
      (row.endsDetailGroup ? DETAIL_GROUP_GAP : 0),
  };
}

function getRowsHeight(doc, rows, width) {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  return rows.reduce(
    (height, row) => height + getRowLayout(doc, row, width).height,
    0
  );
}

function addSide(ctx, title, rows, x, y, width) {
  const { doc } = ctx;

  // subsection title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(20);
  doc.text(title, x, y);

  // spacing below subsection title
  let yy = y + 20;

  rows.forEach((row) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(20);

    const layout = getRowLayout(doc, row, width);
    const labelX = x + layout.detailIndent;
    layout.labelLines.forEach((line, lineIndex) => {
      doc.text(
        line,
        labelX + (lineIndex > 0 ? layout.continuationIndent : 0),
        yy + lineIndex * WRAPPED_LINE_HEIGHT
      );
    });

    if (row.amount !== null && row.amount !== undefined) {
      doc.text(fmtCurrency(row.amount, 2), x + width, yy, {
        align: "right",
      });
    }

    yy += layout.height;
  });

  const total = rows.reduce(
    (sum, row) => sum + Number(row.amount || 0),
    0
  );

  // divider line above total
  doc.line(x, yy - 7, x + width, yy - 7);

  // total row
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);

  doc.text("Total", x, yy + 5);

  doc.text(fmtCurrency(total, 2), x + width, yy + 5, {
    align: "right",
  });

  return yy + 20;
}

export function buildPdfUsesRows(uses = []) {
  return uses.flatMap((row) => {
    const children = Array.isArray(row?.children) ? row.children : [];
    if (children.length === 1) {
      const child = children[0] || {};
      const type = String(child?.type || row?.label || "").trim();
      const description = String(child?.firm || "").trim();

      return [{
        ...row,
        label: type && description ? `${type} – ${description}` : type || description,
        amount: child?.amount,
      }];
    }

    if (!row?.expanded || children.length === 0) return [row];

    return [
      { ...row, amount: null },
      ...children.map((child, index) => ({
        label:
          String(child?.firm || "").trim() ||
          String(child?.type || row?.label || "—").trim(),
        amount: child?.amount,
        isDetail: true,
        ...(index === children.length - 1 ? { endsDetailGroup: true } : {}),
      })),
    ];
  });
}

export function addSourcesUses(ctx, sourcesUses, startY) {
  if (!sourcesUses) return startY;

  const { doc, margins, usableWidth } = ctx;

  const sources = Array.isArray(sourcesUses.sources)
    ? sourcesUses.sources
    : [];

  const uses = buildPdfUsesRows(
    Array.isArray(sourcesUses.uses) ? sourcesUses.uses : []
  );

  const contentHeight = Math.max(
    getRowsHeight(doc, sources, (usableWidth - 30) / 2 - 20),
    getRowsHeight(doc, uses, (usableWidth - 30) / 2 - 20)
  );

  // tighter content-driven height
  const boxHeight =
    14 + // top padding
    20 + // subsection title spacing
    contentHeight + // rows, including wrapped description lines
    12 + // divider + total spacing
    16; // bottom padding

  let y = ensurePageSpace(
    ctx,
    startY,
    boxHeight + 44
  );

  y = addSectionTitle(ctx, "Sources & Uses", y);

  const gap = 30;

  const sideWidth =
    (usableWidth - gap) / 2;

  // rounded borders
  doc.setDrawColor(220);
  doc.setLineWidth(0.8);
  doc.setFillColor(248, 248, 248);

  doc.roundedRect(
    margins.left,
    y,
    sideWidth,
    boxHeight,
    4,
    4,
    "FD"
  );

  doc.roundedRect(
    margins.left + sideWidth + gap,
    y,
    sideWidth,
    boxHeight,
    4,
    4,
    "FD"
  );

  const leftBottom = addSide(
    ctx,
    "Sources",
    sources,
    margins.left + 10,
    y + 18,
    sideWidth - 20
  );

  const rightBottom = addSide(
    ctx,
    "Uses",
    uses,
    margins.left + sideWidth + gap + 10,
    y + 18,
    sideWidth - 20
  );

  return Math.max(
    leftBottom,
    rightBottom,
    y + boxHeight
  ) + PDF_CONTENT_GAP;
}
